import { Feature, Point, Position } from 'geojson';
import type { LatitudeLongitudeFields } from '~/data/LatitudeLongitudeRecord';
import {
  AssetClusterProperties,
  AssetMapMarkerFeatureProps,
} from '../models/super-cluster-markers';
import { Bounds } from '../hooks/use-map-bounds';

/**
 * Padding configuration for map bounds
 * Values can be pixels or percentages (e.g., '50%' for 50% of container height)
 */
export interface MapBoundsPaddingConfig {
  top?: number | string;
  right?: number | string;
  bottom?: number | string;
  left?: number | string;
}

export interface FitBoundsWithPaddingParams {
  map: google.maps.Map;
  bounds: Bounds;
  padding?: MapBoundsPaddingConfig;
}

const getPaddingInPixels = (
  value: number | string | undefined,
  containerDimension: number
): number => {
  if (value === undefined) {
    return 0;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string' && value.includes('%')) {
    const percentage = Number.parseFloat(value);
    return (percentage / 100) * containerDimension;
  }

  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getMapPaddingConfig = (
  padding: MapBoundsPaddingConfig | undefined,
  containerWidth: number,
  containerHeight: number
): google.maps.Padding => ({
  top: getPaddingInPixels(padding?.top, containerHeight),
  right: getPaddingInPixels(padding?.right, containerWidth),
  bottom: getPaddingInPixels(padding?.bottom, containerHeight),
  left: getPaddingInPixels(padding?.left, containerWidth),
});

/**
 * Check if bounds object has valid coordinates (no NaN or Infinity values)
 */
const isValidBounds = (bounds: Bounds): boolean => {
  return (
    Number.isFinite(bounds.north) &&
    Number.isFinite(bounds.south) &&
    Number.isFinite(bounds.east) &&
    Number.isFinite(bounds.west)
  );
};

export const getLatLngBoundsFromPositions = (positions: LatitudeLongitudeFields[]) => {
  if (typeof google === 'undefined' || !google.maps?.LatLngBounds) {
    return null;
  }

  const googleBounds = new google.maps.LatLngBounds();

  for (const pos of positions) {
    if (
      typeof pos.latitude === 'number' &&
      typeof pos.longitude === 'number' &&
      Number.isFinite(pos.latitude) &&
      Number.isFinite(pos.longitude)
    ) {
      googleBounds.extend(new google.maps.LatLng(pos.latitude, pos.longitude));
    }
  }

  if (googleBounds.isEmpty()) {
    return null;
  }

  return googleBounds;
};

export const getBoundsFromPositions = (positions: LatitudeLongitudeFields[]) => {
  const googleBounds = getLatLngBoundsFromPositions(positions);

  // Return null if no valid positions were added
  if (!googleBounds) {
    return null;
  }

  const ne = googleBounds.getNorthEast();
  const sw = googleBounds.getSouthWest();
  if (!ne || !sw) {
    return null;
  }

  return {
    north: ne.lat(),
    south: sw.lat(),
    east: ne.lng(),
    west: sw.lng(),
  };
};

export const fitBoundsWithPadding = ({
  map,
  bounds,
  padding,
}: FitBoundsWithPaddingParams): void => {
  if (!map || !bounds) {
    return;
  }

  // Validate bounds to prevent NaN errors
  if (!isValidBounds(bounds)) {
    console.warn('Invalid bounds detected, skipping fitBounds:', bounds);
    return;
  }

  const containerElement = map.getDiv?.() as HTMLElement | null;
  if (!containerElement) {
    return;
  }

  const containerHeight = containerElement.clientHeight;
  const containerWidth = containerElement.clientWidth;
  const paddingConfig = getMapPaddingConfig(padding, containerWidth, containerHeight);

  requestAnimationFrame(() => {
    map.fitBounds(bounds, paddingConfig);
  });
};

export const getBoundsFromFeatures = (
  features: Feature<Point, AssetMapMarkerFeatureProps | AssetClusterProperties>[]
) => {
  const positions: LatitudeLongitudeFields[] = [];
  for (const feature of features) {
    const coords = feature?.geometry?.coordinates;

    if (Array.isArray(coords) && coords.length >= 2) {
      const [longitude, latitude]: Position = coords;

      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        positions.push({
          latitude,
          longitude,
        });
      }
    }
  }
  return getBoundsFromPositions(positions);
};
